import { useState, useRef, useCallback, useEffect, type FC, type ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MdsDropZone, MdsButton, MdsProgressSpinner, MdsIcon, MdsProgressBar } from '@mds/react';
import { Table, TableCaption, TBody, Tr, Td, TableCellText } from '@mds/web-ui-table';
import { Text } from '@mds/web-ui-text';
import { IconButton } from '@mds/web-ui-icon-button';
import { IconTrashCan } from '@mds/web-ui-icons';
import styles from './UploadPage.module.scss';
import { checkEligibility } from '../../Service/FileUploadService';
import { type AttachedFilesRowData, type ClpError, FileUploadStatus } from '../../types/clpTypes';
import type { ApiRequest } from '../../models/ApiRequest';
import type { ApiResponse, ApiResponseRecord } from '../../models/ApiResponse';
import ClpEligibilityTable from '../ClpEligibilityTable';
import { MAX_NUMBER_OF_ADDRESSES } from '../../util/constants';

type MdsIconColor = 'inverse' | 'interactive' | 'warning' | 'success' | 'error' | 'default';

interface FileStatusIcon {
  iconName: string;
  iconColor: MdsIconColor;
  testId: string;
}

/**
 * Maps a file row's alert variant to its status icon, color, and test id.
 */
const getFileStatusIcon = (variant?: string): FileStatusIcon => {
  switch (variant) {
    case 'success':
      return { iconName: 'ico_checkmark_filled', iconColor: 'success', testId: 'status-icon-success' };
    case 'error':
      return { iconName: 'ico_alert_filled', iconColor: 'error', testId: 'status-icon-error' };
    case 'soft':
      return { iconName: 'ico_alert_triangle', iconColor: 'warning', testId: 'status-icon-warning' };
    default:
      return { iconName: 'ico_ellipsis_circle', iconColor: 'default', testId: 'status-icon' };
  }
};

interface UploadPageProps {
  onErrors: (errors: ClpError[]) => void;
}

const UploadPage: FC<UploadPageProps> = ({ onErrors}) => {
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [attachedFilesRowData, setAttachedFilesRowData] = useState<AttachedFilesRowData[]>([]);
  const [clearableFilesRowData, setClearableFilesRowData] = useState<AttachedFilesRowData[]>([]);
  const [inProgress, setInProgress] = useState<boolean>(false);
  const dropZoneRef = useRef<any>(null);
  const [spinnerOn, setSpinnerOn] = useState(false);
  const [records, setRecords] = useState<ApiResponseRecord[] | null>(null);

  const MAX_NUMBER_OF_FILES = 1;

  /**
   * Creates a single row of data to be displayed in the table,
   * with the given file name, status, and alert message.
   *
   * @param fileName - the name of the file
   * @param status - the status of the file
   * @param alertMessage - the alert message to display
   * @returns a new row of data
   */
  const createSingleFileRowData = useCallback((
    fileName: string,
    status: FileUploadStatus | null,
    alertMessage: string | null,
    showDeleteButton = true
  ) => {
    // Creating a new row of data with just the file name by default
    const newFileRowData: AttachedFilesRowData = [
      {
        value: fileName
      },
      {
      },
      {
      }
    ];

    switch(status) {
      case FileUploadStatus.SUCCESS:
        newFileRowData[0].variant = 'success';
        newFileRowData[0].alertMessage = {
          title: alertMessage,
          variant: 'success',
          accessibleTextIcon: 'success icon'
        };
        newFileRowData[1] = newFileRowData[1] && {
          status: 'success'
        };
        break;
      case FileUploadStatus.FAILED:
        newFileRowData[0].variant = 'error';
        newFileRowData[0].alertMessage = {
          title: alertMessage,
          variant: 'error',
          accessibleTextIcon: 'error icon'
        };
        newFileRowData[1] = newFileRowData[1] && {
          status: 'error'
        };
        break;
      case FileUploadStatus.PARTIAL_SUCCESS:
        newFileRowData[0].variant = 'soft';
        newFileRowData[0].alertMessage = {
          title: alertMessage,
          variant: 'soft',
          accessibleTextIcon: 'warning icon'
        };
        newFileRowData[1] = newFileRowData[1] && {
          status: 'warning'
        };
        break;
    }

    // Add delete button to the end of the row
    if(showDeleteButton && newFileRowData[2]) {
      newFileRowData[2] = {
        buttonItems: [
          {
            tooltipText: 'Delete',
            icon: 'ico_trash_can',
            accessibleText: 'Delete file',
          },
        ],
      };
    }

    return newFileRowData;
  }, []);

  /**
   * Creates a single in-progress row of data to be displayed in the table,
   * with the given file name and progress bar.
   *
   * @param fileName - the name of the file
   * @param progress - the progress percentage of the file
   * @returns a new row of data
   */
  const createSingleFileRowDataWithProgressBar = useCallback((
    fileName: string,
    progress = 0
  ): AttachedFilesRowData => {
    return [
      {
        value: fileName,
        variant: 'informational'
      },
      {
        status: 'inProgress',
        progressBar: {
          progressPercentage: progress,
          accessibleText: `Progress Bar - ${progress}%`,
        }
      },
      {
        buttonItems: null
      }
    ];
  }, []);


  /**
   * Updates the progress of the files, if the files are currently in progress.
   *
   * @param {AttachedFilesRowData[]} attachedFilesRowData - The array of files.
   * @param {number} [increment=20] - The increment value for the progress.
   * @return {AttachedFilesRowData[]} - The updated array of files.
   */
  const updateFilesRowDataProgress = useCallback((
    attachedFilesRowData: AttachedFilesRowData[],
    increment = 20
  ) => {
    const _attachedFilesRowData = [...attachedFilesRowData];

    for(let i = 0; i < _attachedFilesRowData.length; i++) {
      let rowToBeUpdated: AttachedFilesRowData = [..._attachedFilesRowData[i]];

      if(rowToBeUpdated.length >= 1
        && rowToBeUpdated[1]?.status === 'inProgress'
        && rowToBeUpdated[1]?.progressBar
      ) {
        if(rowToBeUpdated[1]?.progressBar.progressPercentage < 100) {
          rowToBeUpdated = createSingleFileRowDataWithProgressBar(
            rowToBeUpdated[0]?.value,
            rowToBeUpdated[1]?.progressBar.progressPercentage + increment
          );
        }
        else {
          rowToBeUpdated = createSingleFileRowData(
            rowToBeUpdated[0].value,
            FileUploadStatus.SUCCESS,
            'Your file uploaded successfully',
          );
        }

        _attachedFilesRowData[i] = rowToBeUpdated;
      }
    }

    return _attachedFilesRowData;
  }, [createSingleFileRowDataWithProgressBar, createSingleFileRowData] );

/**
 * Check if any row in the attachedFilesRowData is in progress.
 *
 * @param {AttachedFilesRowData[]} attachedFilesRowData - The array of row data.
 * @return {boolean} True if any row is in progress, false otherwise.
 */
  const isRowDataInProgress = (
    attachedFilesRowData: AttachedFilesRowData[],
  ) => {
    return attachedFilesRowData.some(
      (rowData) =>
        rowData.length >= 1 && rowData[1]?.status === 'inProgress'
    );
  }

  /**
   * Clears the status (alert icon and message) of all files in the given files list.
   *
   * @param {AttachedFilesRowData[]} attachedFilesRowData - The array of file row data.
   * @return {AttachedFilesRowData[]} The updated array of row data.
   */
  const clearAttachedFileStatuses = useCallback((
    attachedFilesRowData: AttachedFilesRowData[]
  ): AttachedFilesRowData[] => {
    return attachedFilesRowData.map((rowData) =>
      createSingleFileRowData(
        rowData[0].value,
        null,
        null,
        true
      )
    );
  }, [createSingleFileRowData]);

  /**
   * Handles the change event of the file attachment dropzone.
   *
   * - Adds the addedFiles to the attachedFilesRowData with initial progress bars.
   * - Sets the inProgress state to true to indicate that the file attachment is in progress.
   *
   * @param {Event} event - The change event, containing the addedFiles and rejectedFiles from the dropzone.
   * @return {Promise<void>} A promise that resolves when the file upload is complete.
   */
  const handleFileAttachmentChange = useCallback((event: any) => {
    const addedFiles = event.detail.addedFiles;
    const rejectedFiles = event.detail.rejectedFiles;
    const fileAttachmentErrors : ClpError[] = [];

    // Clears the clearable file names because the user has already had a chance to read them
    setClearableFilesRowData([]);

    // Provides info about files that were rejected from being attached
    if (rejectedFiles && rejectedFiles.length) {
      const error: ClpError = {
        type: 'file',
        subtype: '',
        message: `Couldn't attach the following files - check your file format and try again:
          ${rejectedFiles.map((file: { name: any; }) => file.name).join(', ')}`
      };
      fileAttachmentErrors.push(error);
    }

    // Adds the addedFiles to the attachedFilesRowData with initial progress bars
    if (addedFiles && addedFiles.length) {
      const _attachedFilesRowData = [...attachedFilesRowData];

      for(const addedFile of addedFiles) {
        _attachedFilesRowData.push(
          createSingleFileRowDataWithProgressBar(
            addedFile.name
          )
        );
      }

      setAttachedFilesRowData(_attachedFilesRowData);
      setInProgress(true);
      setAttachedFiles(addedFiles);
    }

    // Sets the errors at the end of the function to either show the info about the rejected files, or clear the errors if there are none
    onErrors(fileAttachmentErrors);
  }, [attachedFiles, attachedFilesRowData, createSingleFileRowDataWithProgressBar, onErrors]);


  /**
   * Handle file attachment errors.
   *
   * This function is called whenever there is an error in the file
   * attachment process. It logs the error to the console and sets the
   * errors state to the error message.
   *
   * @param {Event} event - The event object containing the error message.
   */
  const handleFileAttachmentError = useCallback((event: any) => {
    const errorMessage: string = event.detail['error message'];
    const clpError: ClpError = {
      type: 'ui',
      subtype: '',
      message: errorMessage
    };
    onErrors([clpError]);
    setClearableFilesRowData([]);
  }, [onErrors]);

  /**
   * Deletes the file from both the dropzone and the file list.
   *
   * @param {number} rowToBeDeleted - The index of the row to be deleted.
   * @return {void}
   */
  const deleteFileRowData = useCallback((rowToBeDeleted: number) => {
    const _attachedFiles = [...attachedFiles];
    const _attachedFilesRowData = [...attachedFilesRowData];

    // Removes the file from both the dropzone and the file list
    _attachedFiles.splice(rowToBeDeleted, 1);
    _attachedFilesRowData.splice(rowToBeDeleted, 1);
    dropZoneRef?.current?._ref?.current?.deleteFile(rowToBeDeleted);

    // Updates the state of the both the dropzone and the file list
    setAttachedFiles(_attachedFiles);
    setAttachedFilesRowData(_attachedFilesRowData);
  }, [attachedFiles, attachedFilesRowData]);

  /**
   * Handles the delete button click event - results in the deletion of the file from both the dropzone and the file list.
   *
   * @param {number} rowToBeDeleted - The index of the file row to delete.
   * @return {void}
   */
  const handleDelete = useCallback((rowToBeDeleted: number) => {
    // Removes the file from both the dropzone and the file list
    deleteFileRowData(rowToBeDeleted);

    // Clears the messages
    onErrors([]);
  }, [deleteFileRowData, onErrors]);

  /**
   * Handles the Check Eligibility button click event.
   *
   * @return {Promise<void>} A promise that resolves when the file eligibility check is complete.
   */
  const handleFileSubmit = useCallback(async () => {
    const rowToBeChecked = 0;
    const fileToBeChecked = attachedFiles[rowToBeChecked];
    const newClearableFilesRowData: AttachedFilesRowData[] = [];
    const correlationId = uuidv4();

    setSpinnerOn(true);

    // Notice that for now we only process the first file in the list. This is because the API only accepts one file.
    // We can add support for multiple files in the future to this function if required.
    try {
      const apiRequest: ApiRequest = {
        correlationId
      };
      const result: ApiResponse = await checkEligibility(fileToBeChecked, apiRequest);
      setRecords(result.records);

      // Gets the status of the file from the API response
      let fileUploadStatus: FileUploadStatus;
      if (result.success === FileUploadStatus.SUCCESS.valueOf()) {
        fileUploadStatus = FileUploadStatus.SUCCESS;
      }
      else if (result.success === FileUploadStatus.PARTIAL_SUCCESS.valueOf()) {
        fileUploadStatus = FileUploadStatus.PARTIAL_SUCCESS;
      }
      else {
        fileUploadStatus = FileUploadStatus.FAILED;
      }

      // Adds the file to the list of clearable files with the data from the API response
      newClearableFilesRowData.push(createSingleFileRowData(
        fileToBeChecked.name,
        fileUploadStatus,
        result.message,
        false
      ));

      // Clears the error messages and clears the checked files from the attached files list
      deleteFileRowData(rowToBeChecked);
      onErrors([]);
    // The following handles errors from the API call - includes both file-level errors and 500 server errors
    // Also clears the statuses of the attached files
    } catch (error: any) {
      setAttachedFilesRowData(clearAttachedFileStatuses(attachedFilesRowData));
      const errorMessage:string = error?.response?.data?.errors ?? error?.response?.data?.message ?? 'Error uploading files';
      const errorCode = error?.response?.status ?? error?.status;
      const clpError: ClpError = {
        type: 'system',
        subtype: 'check',
        message: errorMessage
      };

      if (errorCode === 400) {
        clpError.type = 'file'
        clpError.subtype = ''
      }

      onErrors([clpError]);
    }

    setClearableFilesRowData(newClearableFilesRowData);
    setSpinnerOn(false);
  }, [
    attachedFiles,
    attachedFilesRowData,
    createSingleFileRowData,
    deleteFileRowData,
    clearAttachedFileStatuses,
    onErrors
  ]);

  /**
   * Handles errors in the ClpEligibilityTable component
   *
   * @param {ClpError[]} errors - The errors to be handled
   * @return {void}
   */
  const handleClpEligibilityTableErrors = (errors: ClpError[]) => {
    onErrors(errors);
  };

  /**
   * Renders the table rows for a list of attached or checked files, showing the
   * file name, an upload progress bar or status icon + message, and (for attached
   * files) a delete button.
   *
   * @param {AttachedFilesRowData[]} rowData - The file rows to render.
   * @return {JSX.Element[]} The rendered table rows.
   */
  const renderFileRows = (rowData: AttachedFilesRowData[]) =>
    rowData.map((row, index) => {
      const fileName = row[0].value;
      const alertMessage = row[0].alertMessage;
      const progressBar = row[1]?.progressBar;
      const hasDelete = Boolean(row[2]?.buttonItems);
      const statusIcon = alertMessage ? getFileStatusIcon(alertMessage.variant) : null;

      let statusCell: ReactNode = null;
      if (progressBar) {
        statusCell = (
          <div className={styles.progressBar}>
            <MdsProgressBar
              progressPercentage={progressBar.progressPercentage}
              accessibleText={progressBar.accessibleText}
            />
          </div>
        );
      } else if (statusIcon && alertMessage) {
        statusCell = (
          <span className={styles.statusMessage} data-testid={statusIcon.testId}>
            <MdsIcon type={statusIcon.iconName} color={statusIcon.iconColor} size={20} />
            <Text primitive="span" textStyle="bodyMediumHeavier">{alertMessage.title}</Text>
          </span>
        );
      }

      return (
        <Tr
          key={`${fileName}-${index}`}
          borderStyle={index < rowData.length - 1 ? 'dashed' : 'none'}
        >
          <Td verticalAlign="center">
            <div className={styles.fileCell}>
              <TableCellText text={<span className={styles.fileName}>{fileName}</span>} />
              {statusCell}
            </div>
          </Td>
          <Td align="center" verticalAlign="center">
            {hasDelete && (
              <IconButton
                aria-label="Delete file"
                icon={<IconTrashCan aria-hidden />}
                variant="tertiary"
                onClick={() => handleDelete(index)}
              />
            )}
          </Td>
        </Tr>
      );
    });

  /**
   * This effect is used to update the progress of the file attachment.
   * It is triggered when inProgress is turned on, and there is at least
   * one file being attached to the drop zone.
   *
   * It is set to run every 0.2 seconds as long as the attchment is in progress
   *
   * Once the file is attached, the inProgress state is turned off.
   * Currently this runs purely on a timer.
   */
  useEffect(() => {
    const timer = setInterval(() => {
      if(inProgress && isRowDataInProgress(attachedFilesRowData)) {
        setAttachedFilesRowData((prevAttachedFilesRowData) => {
          return updateFilesRowDataProgress(prevAttachedFilesRowData);
        });
      }
      else {
        setInProgress(false);
        clearInterval(timer);
      }
    }, 200);

    return () => {
      clearInterval(timer);
    };
  }, [inProgress, attachedFilesRowData, updateFilesRowDataProgress]);

  return (
    <div className={styles.uploadPage}>
      <MdsProgressSpinner
        data-testid="mds-progress-spinner"
        id="mds-progress-spinner"
        loadingLabel="Just a moment while we check property eligibility"
        microcopy="microcopy"
        open={spinnerOn}
      />

      {/*<ErrorDisplay errors={errors} />*/}
      <div className={styles['dropzone-container']}>
        <div className={styles['dropzone-wrapper']}>
          <MdsDropZone
            id="mds-drop-zone-clp"
            name="clp-eligbility-drop-zone"
            ref={dropZoneRef}
            maxNumberOfFiles={MAX_NUMBER_OF_FILES}
            maxNumberOfFilesErrorMessage={`You can only attach ${MAX_NUMBER_OF_FILES} file${MAX_NUMBER_OF_FILES > 1 ? 's' : ''}`}
            fieldLabel="Attach"
            helperTextTitle="Attach your files"
            microcopy={[
              `Format: .xlsx and .xls.`,
              `Upload ${MAX_NUMBER_OF_FILES > 1 ? 'up to' : ''} ${MAX_NUMBER_OF_FILES} file${MAX_NUMBER_OF_FILES > 1 ? 's' : ''} with up to ${MAX_NUMBER_OF_ADDRESSES} addresses.`
            ]}
            uploadFileButtonText="Drag and drop files or browse"
            fileTypes={[
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
              'application/vnd.ms-excel' // .xls
            ]}
            maxFileSizeErrorMessage="File size exceeds the maximum allowed size"
            containerWidth="100%" // Ensure the dropzone takes full width
            onChange={handleFileAttachmentChange}
            onError={handleFileAttachmentError}
            data-testid="mds-drop-zone-clp" // Add data-testid attribute
          />
          {(attachedFiles?.length > 0 || clearableFilesRowData?.length > 0) && (
            <div className={styles['data-table-for-files-container']}>
              {attachedFiles?.length > 0 && (
                <Table
                  id="attached-files-table"
                  data-testid="attached-files-table"
                  density="relaxed"
                >
                  <TableCaption hideText text="Files attached" />
                  <TBody>
                    {renderFileRows(attachedFilesRowData)}
                  </TBody>
                </Table>
              )}
              {clearableFilesRowData?.length > 0 && (
                <Table
                  id="clearable-files-table"
                  data-testid="clearable-files-table"
                  density="relaxed"
                >
                  <TableCaption hideText text="Files checked" />
                  <TBody>
                    {renderFileRows(clearableFilesRowData)}
                  </TBody>
                </Table>
              )}
            </div>
          )}
        </div>
        <div className={styles['button-container']}>
          <MdsButton
            data-testid="mds-button"
            id="elgibilitycheck"
            text="Check Eligibility"
            variant="primary"
            widthType="content"
            small={false}
            accessibleText="Check Eligibility"
            inactive={attachedFiles?.length === 0 || inProgress} // Button is inactive if no file is attached, or if a file is currently being attached
            type="button"
            inverse={false}
            onClick={handleFileSubmit} // Handle file submission on button click
          />
        </div>
      </div>
      <div>
        <ClpEligibilityTable records={records} onError={handleClpEligibilityTableErrors}></ClpEligibilityTable>
      </div>
    </div>
  );
}

export default UploadPage;
